import React, { useEffect } from 'react';
import { Spinner, FlexBox } from '@vp/swan';
import { initializeNewRelicTrackingContainer } from '@vp/om-newrelic-tracker';
import { useTranslations } from '@vp/om-translations';
import { useDispatch } from 'react-redux';
import useIdentityContext from '../hooks/useIdentityContext';
import useSiteData from '../hooks/useSiteData';
import { InternalViewCartSession } from './internalView/InternalViewCartSession';
import { CartApplication } from './CartApplication';
import { FORCED } from '../constants/authTracking';
import { siteActions } from '../state/site';
import { CART_NEWRELIC_BROWSER_AGENT_IDENTIFIER } from '../constants/newRelic';

export const CartSession = () => {
  const { allowAnonymousCartSessions, vistacartTenant, siteName, locale } =
    useSiteData();
  const [cartMessages] = useTranslations(['cartMessages']);
  const { isIdentityInitialized, identity, auth } = useIdentityContext();
  const dispatch = useDispatch();

  const {
    isShopper,
    isInternalUser,
    isAnonymousUser,
    shopperId,
    anonymousUserId,
  } = identity;

  useEffect(() => {
    dispatch(siteActions.SITE_LOAD_SUCCESS({ siteName }));
  }, [siteName, dispatch]);

  useEffect(() => {
    // don't bother initializing the tracking container until the properties are all available
    if (
      (shopperId || anonymousUserId) &&
      locale &&
      siteName &&
      vistacartTenant
    ) {
      initializeNewRelicTrackingContainer({
        agentId: CART_NEWRELIC_BROWSER_AGENT_IDENTIFIER,
        staticApplicationData: {
          shopperId: shopperId || anonymousUserId,
          locale,
          site: siteName,
          tenant: vistacartTenant,
        },
      });
    }
  }, [anonymousUserId, locale, shopperId, siteName, vistacartTenant]);

  // Session Identity unknown, display Preloader as auth does its thing.
  if (!isIdentityInitialized)
    return (
      <FlexBox justifyContent="center">
        <Spinner
          size="super"
          accessibleText={cartMessages.loading}
          aria-busy="true"
          aria-live="polite"
          role="status"
          my={9}
        />
      </FlexBox>
    );

  if (isInternalUser) return <InternalViewCartSession />;

  if (isShopper) {
    return (
      <CartApplication
        tenant={vistacartTenant}
        shopperId={shopperId}
        locale={locale}
      />
    );
  }

  if (isAnonymousUser) {
    if (allowAnonymousCartSessions) {
      return (
        <CartApplication
          tenant={vistacartTenant}
          shopperId={anonymousUserId}
          locale={locale}
        />
      );
    }
    auth.signIn([], [], { trackingCaller: FORCED });
    return (
      <FlexBox justifyContent="center">
        <Spinner
          size="super"
          accessibleText={cartMessages.loading}
          aria-busy="true"
          aria-live="polite"
          role="status"
          my={9}
        />
      </FlexBox>
    );
  }

  console.warn(
    'Cart Session Identity Status Unknown. No Cart Application will be rendered.'
  );
  return null;
};
